import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { getCreativeSuffix, parseAmount } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import PopupBasic from '../components/popups/PopupBasic';
import { PetEntity } from '../logic/EntityManager';
import * as History from 'src/replicant/history';
import StateObserver from 'src/StateObserver';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import uiConfig from 'src/lib/ui/config';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';

import { AssetGroup } from '@play-co/timestep-core';

/**
 * Popup encyclopedia details
 */
export default class PopupEncyclopediaDetails extends PopupBasic {
  private name: LangBitmapFontTextView;
  private pet: PetEntity;
  private stage: (data: string) => void;
  private timesRaised: (data: string) => void;
  private description: LangBitmapFontTextView;

  /**
   * Constructor
   */
  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      width: 627,
      height: 900,
      darkerBg: true,
      skipMessage: true,
      titleIcon: {
        image: 'assets/ui/encyclopedia/icon/encyclopedia_header_icon.png',
        width: 90,
        height: 80,
        offsetX: 20,
        offsetY: 4,
      },
      tracking: {
        feature: Feature.ENCYCLOPEDIA._,
        $subFeature: Feature.ENCYCLOPEDIA.POPUP_DETAILS._,
      },
    });

    this.title.updateOpts({ size: 30, offsetX: 30 });
    this.buttonClose.removeFromSuperview();

    this.name = new LangBitmapFontTextView({
      superview: this.box,
      color: '#9A7171',
      font: 'Body',
      size: 36,
      width: 531,
      height: 40,
      x: this.box.style.width / 2,
      y: 114,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
    });

    new View({
      superview: this.box,
      backgroundColor: '#FDC381',
      width: 480,
      height: 5,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 150,
    });

    // Pet preview with animation
    this.pet = new PetEntity('preview', 'ball');
    this.pet.updateOpts({ x: 300, y: 447 });
    this.pet.setAnimation(petAnimationNames['ball'].idle);
    this.box.addSubview(this.pet);

    // Pet stage
    this.stage = this.createLabel(this.box, {
      text: i18n('encyclopedia.details.growthStage'),
      y: 490,
      width: 480,
    });

    // Times raised
    this.timesRaised = this.createLabel(this.box, {
      text: i18n('encyclopedia.details.timesRaised'),
      y: 549,
      width: 480,
    });

    // Pet description
    this.description = new LangBitmapFontTextView({
      superview: this.box,
      color: '#BF8147',
      font: 'Body',
      size: 28,
      width: 480,
      height: 145,
      wordWrap: true,
      x: this.box.style.width / 2,
      y: 688,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      labelOffsetY: -1,
      fontSize: 46,
      width: 240,
      height: 100,
      centerOnOrigin: true,
      font: 'Body',
      superview: this.box,
      x: this.box.style.width / 2,
      y: 832,
      localeText: () => i18n('basic.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: {
        feature: Feature.ENCYCLOPEDIA._,
        $subFeature: Feature.ENCYCLOPEDIA.POPUP_DETAILS.BACK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/encyclopedia/icon/encyclopedia_header_icon.png',
      'assets/ui/decoration/frames/rectangle_light.png',
    ]);
  }

  /**
   * On dialog show
   */
  public init(opts: { species: PetSpeciesID }): void {
    super.init({
      title: i18n('encyclopedia.popup.title'),
    });

    const { species } = opts;
    const { user } = StateObserver.getState();
    const history = History.getters.getRaisedPetsHistory(user);
    const raised = history[species] ?? 0;
    const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user)];

    // Update name
    this.name.localeText = () =>
      evolutions[species]?.[`name_${getCreativeSuffix()}`];

    // Update pet
    this.pet.setSpecies(species);
    this.pet.setAnimation(petAnimationNames[species].idle);

    // Set pet details
    this.stage(i18n(`stage.${evolutions[species].stage}`));
    this.timesRaised(
      i18n('encyclopedia.details.timesRaisedValue', {
        times: parseAmount(raised),
      }),
    );

    // Description
    this.description.localeText = () =>
      evolutions[species]?.[`description_${getCreativeSuffix()}`];
  }

  /**
   * Create label for pet details
   */
  private createLabel(
    superview: View,
    opts: { text: string; y: number; width: number },
  ) {
    const frame = new ImageScaleView({
      tag: 'frame',
      superview,
      x: (superview.style.width - 481) * 0.5,
      y: opts.y,
      width: opts.width,
      height: 47,
      image: 'assets/ui/decoration/frames/rectangle_light.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 43, right: 43 },
      },
    });

    new LangBitmapFontTextView({
      tag: 'label',
      superview: frame,
      x: 20,
      y: 0,
      width: 300,
      height: 47,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      color: '#BF8147',
      localeText: () => opts.text,
      font: bitmapFonts('Body'),
    });

    const dataView = new LangBitmapFontTextView({
      tag: 'price',
      superview: frame,
      x: opts.width - 260,
      y: 0,
      width: 200,
      height: 47,
      align: 'right',
      verticalAlign: 'center',
      size: 24,
      color: '#9A7171',
      localeText: () => '',
      font: bitmapFonts('Body'),
    });

    return (data: string) => {
      dataView.localeText = () => data;
    };
  }
}
